import React, { useState, useEffect } from 'react';
import { fetchAddressValidations, fetchUnitScreenFlags, resolveUnitScreenFlag } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';
import { toTitleCase } from '../utils/format';

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

const todayIso = new Date().toISOString().split('T')[0];
const PAGE_SIZE = 50;

// Orders the missing-unit screen (netlify/functions/lib/unit-screen.js) flagged
// BEFORE the label printed. Each one is also emailed to the label-check list;
// this is where somebody marks it handled so the list stays a to-do list.
function MissingUnitFlags({ navigateTo }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [showDone, setShowDone] = useState(false);
  const load = () => fetchUnitScreenFlags(14).then(setRows).catch(e => setErr(e.message));
  useEffect(() => { load(); }, []);

  const toggle = async (row, resolved) => {
    const note = resolved ? (window.prompt('What was done? (optional)', row.suggested_unit ? `Added ${row.suggested_unit}` : '') ?? null) : null;
    if (resolved && note === null) return;
    try { await resolveUnitScreenFlag(row.bc_order_id, resolved, note || null); load(); } catch (e) { setErr(e.message); }
  };

  const list = (rows || []).filter(r => showDone || !r.resolved_at);
  const open = (rows || []).filter(r => !r.resolved_at).length;
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Address problems caught before shipping {rows && `(${open} open)`}</span>
        <label style={{ fontSize: 11, fontWeight: 500, display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={showDone} onChange={e => setShowDone(e.target.checked)} /> Show handled
        </label>
      </div>
      <div style={{ padding: '8px 16px 0', fontSize: 11.5, color: 'var(--gray-500)' }}>
        Every unshipped order is checked for a missing apartment number, an address USPS cannot find, and an address UPS has redirected before.
      </div>
      {err && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {err}</div>}
      {!rows && !err && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
      {rows && list.length === 0 && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Nothing flagged in the last 14 days.</div>}
      {list.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse', marginTop: 8 }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--gray-500)' }}>Order</th>
                <th style={{ textAlign: 'left', padding: '8px 8px', color: 'var(--gray-500)' }}>Ship to</th>
                <th style={{ textAlign: 'left', padding: '8px 8px', color: 'var(--gray-500)' }}>Why</th>
                <th style={{ textAlign: 'left', padding: '8px 8px', color: 'var(--gray-500)' }}>Likely unit</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: 'var(--gray-500)' }}></th>
              </tr>
            </thead>
            <tbody>
              {list.map(r => (
                <tr key={r.bc_order_id} style={{ borderBottom: '1px solid var(--gray-100)', opacity: r.resolved_at ? 0.55 : 1 }}>
                  <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600, cursor: 'pointer' }}
                      onClick={() => navigateTo?.('order-details', r.bc_order_id)}>{r.bc_order_id}</td>
                  <td style={{ padding: '8px 8px', color: 'var(--navy)' }}>
                    {toTitleCase(r.customer || '')}{r.ship_company ? ` / ${r.ship_company}` : ''}
                    <div style={{ color: 'var(--gray-500)', fontSize: 11 }}>{[r.ship_line1, r.ship_line2, r.ship_city, r.ship_state, r.ship_zip].filter(Boolean).join(', ')}</div>
                  </td>
                  <td style={{ padding: '8px 8px', color: 'var(--gray-700)', fontSize: 11 }}>
                    {(r.reasons || []).map((x, i) => <div key={i}>{x.detail}</div>)}
                  </td>
                  <td style={{ padding: '8px 8px', fontWeight: 700, color: 'var(--navy)' }}>{r.suggested_unit || '—'}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {r.resolved_at
                      ? <span style={{ fontSize: 11, color: 'var(--gray-500)' }} title={r.resolved_note || ''}>Handled <button className="btn btn-navy" style={{ fontSize: 10, marginLeft: 6 }} onClick={() => toggle(r, false)}>Reopen</button></span>
                      : <button className="btn btn-primary" style={{ fontSize: 11 }} onClick={() => toggle(r, true)}>Mark handled</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AddressValidations({ navigateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(todayIso);
  const [reviewed, setReviewed] = useState('');
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    setError(null);
    const params = {
      StartDate: toApiDate(start),
      EndDate: toApiDate(end),
      PageNumber: page,
      PageSize: PAGE_SIZE,
      ...(reviewed !== '' ? { ValidationReviewed: reviewed === 'true' } : {}),
    };
    fetchAddressValidations(params)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page]);

  const rows = Array.isArray(data) ? data : data?.data ?? data?.records?.data ?? [];
  const total = data?.totalRows ?? data?.total ?? rows.length;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Shipping Address Validations</h2>
      </div>

      <MissingUnitFlags navigateTo={navigateTo} />

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Filters</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <DateRangeFilter start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} />
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>REVIEW STATUS</div>
            <select value={reviewed} onChange={e => setReviewed(e.target.value)} className="form-select" style={{ fontSize: 11 }}>
              <option value="">All</option>
              <option value="false">Needs Review</option>
              <option value="true">Reviewed</option>
            </select>
          </div>
          <button onClick={() => { setPage(1); load(); }} className="btn btn-primary" style={{ fontSize: 11 }}>Search</button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Address Validation Issues {total > 0 && `(${total.toLocaleString()})`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No address validation issues found.</div>
        )}
        {rows.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order ID</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship Address</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship Type</th>
                    <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Reviewed</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} onClick={() => navigateTo?.('order-details', row.bcOrderId ?? row.orderId)} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}>
                      <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{row.bcOrderId ?? row.orderId ?? '—'}</td>
                      <td style={{ padding: '8px 8px', color: 'var(--navy)' }}>{toTitleCase(row.billTo ?? row.customerName ?? '—')}</td>
                      <td style={{ padding: '8px 8px', color: 'var(--gray-700)', fontSize: 11 }}>
                        {[row.shipAddress, toTitleCase(row.shipCity), row.shipState, row.shipZip].filter(Boolean).join(', ') || row.shipTo || '—'}
                      </td>
                      <td style={{ padding: '8px 8px', color: 'var(--gray-500)' }}>{row.shipType ?? '—'}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: row.validationReviewed ? '#e6f9f0' : '#fff8e6', color: row.validationReviewed ? '#1a8a4a' : '#b06a00' }}>
                          {row.validationReviewed ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>
                        {row.shipDate ?? row.startDate ? new Date(row.shipDate ?? row.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {total > PAGE_SIZE && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 12 }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn btn-navy" style={{ fontSize: 11 }}>← Prev</button>
                <span style={{ fontSize: 11, alignSelf: 'center' }}>Page {page}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={rows.length < PAGE_SIZE} className="btn btn-navy" style={{ fontSize: 11 }}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
